import React, {useEffect, useState} from 'react';
import {View} from 'react-native';

import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import Spacer from '@/Atoms/Spacer';
import {vs} from '@/Utils/Dimensions';
import {styles as settingsStyles} from '@/Screens/Settings/styles';

import useAddReferralCodeApi from '@/Hooks/ReferralApi/useAddReferralCodeApi';
import useGetMyReferralCodeApi from '@/Hooks/ReferralApi/useGetMyReferralCodeApi';

type ReferralCodeEntryProps = {
  // Settings/index.tsx derives this from the live viewport (isMobileOrTab),
  // not from the static styles.section -- every other section on the page
  // now takes it the same way, so this must too or it visually falls out of
  // step (wrong padding) on mobile/tablet widths. Falls back to the static
  // styles.section if a caller doesn't pass one.
  sectionStyle?: any;
};

/**
 * Lets a user who signed up via a plain URL by mistake add a referral code
 * retroactively. The 14-day-from-signup window and "already attributed" guard
 * are enforced server-side (auth/v1/referral/add_code, chatbot repo) -- this
 * component doesn't re-derive that from the user's created_at (the profile
 * object is shared across multiple screens and re-deriving the window in each
 * one risks drifting out of sync). Instead it fetches the same
 * can_add_referral_code flag GET my_code already computes server-side.
 *
 * Not applicable (already attributed, or the window closed) means nothing to
 * do here -- the whole section (including its own heading) hides itself
 * rather than showing an explanation, which is why this owns its own section
 * wrapper/heading instead of Settings/index.tsx rendering them unconditionally.
 * See docs/referral-program-plan.md.
 */
const ReferralCodeEntry: React.FC<ReferralCodeEntryProps> = ({
  sectionStyle,
}) => {
  const wrapperStyle = sectionStyle || settingsStyles?.section;
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [done, setDone] = useState(false);

  const {
    mutate: fetchEligibility,
    data: eligibility,
    isLoading: isLoadingEligibility,
  } = useGetMyReferralCodeApi();

  useEffect(() => {
    fetchEligibility();
  }, []);

  const {mutate: addReferralCode, isLoading} = useAddReferralCodeApi(
    data => {
      if (data?.success) {
        setMessage({type: 'success', text: 'Referral code added!'});
        setDone(true);
      } else {
        setMessage({type: 'error', text: data?.error || 'Could not add code'});
      }
    },
    error => {
      setMessage({
        type: 'error',
        text: error?.error || 'Could not add code',
      });
    },
  );

  const handleSubmit = () => {
    if (!code.trim()) return;
    setMessage(null);
    addReferralCode({referral_code: code.trim()});
  };

  if (done) {
    return (
      <View style={wrapperStyle}>
        <CCText style={settingsStyles?.sectionHeader}>Referral Code</CCText>
        <Spacer spacing={vs(12)} />
        <CCText color={CCColors.Green}>Referral code added!</CCText>
      </View>
    );
  }

  // Not (yet) loaded, already attributed, or the window closed -- nothing to
  // do here, so hide the entire section (heading included) rather than
  // showing a message for something the user can no longer act on.
  if (
    isLoadingEligibility ||
    !eligibility ||
    eligibility.referral_code_already_attributed ||
    !eligibility.can_add_referral_code
  ) {
    return null;
  }

  return (
    <View style={wrapperStyle}>
      <CCText style={settingsStyles?.sectionHeader}>Referral Code</CCText>
      <Spacer spacing={vs(12)} />
      <CCText fontSize={13} color={CCColors.TextColor.Grey}>
        Missed adding a friend's referral code when you signed up? You can add it
        here within 14 days of joining CircleChess.
      </CCText>
      <View
        style={{
          flexDirection: 'row',
          gap: 12,
          marginTop: 8,
          flexWrap: 'wrap',
        }}>
        <CCTextInput
          value={code}
          onChangeText={setCode}
          placeholder="Enter referral code"
          style={{flexGrow: 1, minWidth: 160}}
        />
        <CCButton
          type="fill"
          loading={isLoading}
          disabled={!code.trim() || isLoading}
          onPress={handleSubmit}>
          Add Code
        </CCButton>
      </View>
      {message && (
        <CCText
          fontSize={13}
          color={message.type === 'error' ? CCColors.RedBg : CCColors.Green}
          style={{marginTop: 8}}>
          {message.text}
        </CCText>
      )}
    </View>
  );
};

export default ReferralCodeEntry;
